import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updatePromoFields } from "../../../../store/slices/promoFormSlice";
import { Select, Input, Tag, Tooltip } from "antd";
import { LinkOutlined, WalletOutlined, BlockOutlined, CopyOutlined } from "@ant-design/icons";
import { message } from "antd";
import blockchainApi from "../../../../api/blockchainApi";

const CHAINS = [
    { value: 31337, label: "Hardhat Testnet" },
];

const ROLE_COLORS = {
    deployer: "red",
    signer: "orange",
    platform: "blue",
    promoter: "green",
    user: "cyan",
};

export default function PromoBlockchain() {
    const dispatch = useDispatch();
    const promoForm = useSelector((s) => s.promoForm);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        blockchainApi
            .getConfig()
            .then((res) => {
                const data = res.data || res;
                setConfig(data);
                if (!promoForm.chainId && data.chainId) {
                    dispatch(updatePromoFields({ chainId: data.chainId }));
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const tokens = config?.tokens || [];
    const accounts = config?.accounts || [];
    const promoterAccounts = accounts.filter((a) => a.role === "promoter");

    const short = (addr) => addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : "";

    const handleSelectToken = (token) => {
        dispatch(
            updatePromoFields({
                tokenContractAddress: token.address,
                tokenType: token.address,
                tokenSymbol: token.symbol,
                tokenLogoUrl: null,
            })
        );
        message.success(`${token.name} (${token.symbol}) selected`);
    };

    const handleSelectWallet = (account) => {
        dispatch(updatePromoFields({ promoterAddress: account.address }));
        message.success(`${account.label} selected`);
    };

    return (
        <div className="flex w-full flex-col gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur-xl">
            <div className="flex w-full flex-row items-center gap-2 pl-2">
                <div className="flex items-center gap-1 rounded-lg border border-purple-400/40 bg-purple-500/15 px-2 py-0.5 text-sm font-medium text-purple-100">
                    <BlockOutlined /> Blockchain
                </div>
                {promoForm.chainId && (
                    <Tag color="green" className="m-0">
                        Chain {promoForm.chainId}
                    </Tag>
                )}
            </div>

            <div className="flex flex-col gap-2">
                <label className="text-xs text-white/45">Network</label>
                <Select
                    value={promoForm.chainId}
                    onChange={(val) => dispatch(updatePromoFields({ chainId: val }))}
                    options={CHAINS}
                    placeholder="Select blockchain network"
                    className="w-full"
                    loading={loading}
                    size="small"
                />
            </div>

            {/* Prize Token */}
            <div className="flex flex-col gap-2">
                <label className="text-xs text-white/45">Prize Token Contract</label>
                {tokens.length > 0 && (
                    <div className="flex flex-row gap-1 flex-wrap">
                        {tokens.map((t) => (
                            <Tag
                                key={t.address}
                                color={promoForm.tokenContractAddress === t.address ? "green" : "default"}
                                className="cursor-pointer"
                                onClick={() => handleSelectToken(t)}
                            >
                                {t.symbol} ({t.decimals} dec)
                            </Tag>
                        ))}
                    </div>
                )}
                <Input
                    value={promoForm.tokenContractAddress || ""}
                    onChange={(e) =>
                        dispatch(updatePromoFields({ tokenContractAddress: e.target.value }))
                    }
                    placeholder="0x..."
                    prefix={<LinkOutlined />}
                    size="small"
                    allowClear
                />
                {promoForm.tokenContractAddress && tokens.length > 0 && (() => {
                    const matched = tokens.find((t) => t.address === promoForm.tokenContractAddress);
                    return matched ? (
                        <span className="text-[11px] text-emerald-300/90">
                            {matched.name} ({matched.symbol}) — {matched.decimals} decimals
                        </span>
                    ) : null;
                })()}
            </div>

            {/* Promoter Wallet */}
            <div className="flex flex-col gap-2">
                <label className="text-xs text-white/45">Promoter Wallet Address</label>
                {promoterAccounts.length > 0 && (
                    <div className="flex flex-row gap-1 flex-wrap">
                        {promoterAccounts.map((a) => (
                            <Tag
                                key={a.address}
                                color={promoForm.promoterAddress === a.address ? "green" : "default"}
                                className="cursor-pointer"
                                onClick={() => handleSelectWallet(a)}
                            >
                                {a.label} ({short(a.address)})
                            </Tag>
                        ))}
                    </div>
                )}
                <Input
                    value={promoForm.promoterAddress || ""}
                    onChange={(e) =>
                        dispatch(updatePromoFields({ promoterAddress: e.target.value }))
                    }
                    placeholder="0x..."
                    prefix={<WalletOutlined />}
                    size="small"
                    allowClear
                />
            </div>

            {/* All Hardhat Accounts */}
            {accounts.length > 0 && (
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-white/45">Hardhat Test Accounts</label>
                    <div className="flex flex-col gap-0.5 max-h-[120px] overflow-y-auto pr-1">
                        {accounts.map((a) => (
                            <div
                                key={a.address}
                                className="flex items-center gap-1 rounded px-1 py-0.5 text-[11px] text-white/70 hover:bg-white/[0.06]"
                            >
                                <Tag color={ROLE_COLORS[a.role] || "default"} className="m-0 text-[10px] leading-tight">
                                    {a.role}
                                </Tag>
                                <span className="font-medium">{a.label}</span>
                                <Tooltip title={a.address}>
                                    <span className="font-mono text-white/40">{short(a.address)}</span>
                                </Tooltip>
                                <CopyOutlined
                                    className="ml-auto cursor-pointer text-white/40 hover:text-white"
                                    onClick={() => {
                                        navigator.clipboard.writeText(a.address);
                                        message.success("Copied!");
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Contract addresses */}
            {config && (
                <div className="flex flex-col gap-0.5 border-t border-white/10 pt-2 text-[10px] text-white/40">
                    <Tooltip title={config.giveawayPool}>
                        <span>GiveawayPool: {short(config.giveawayPool)}</span>
                    </Tooltip>
                    <Tooltip title={config.feeCollector}>
                        <span>FeeCollector: {short(config.feeCollector)}</span>
                    </Tooltip>
                </div>
            )}
        </div>
    );
}
